import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Pick } from "@/components/vi/SaleDialog";
import ViMap from "@/components/vi/ViMap";
import { call, money } from "@/lib/vi";
import { RefreshCw, Warehouse, BellRing } from "lucide-react";
import ZoneEditor from "@/components/vi/ZoneEditor";

const ALERT_LABELS = { hors_zone: "Hors zone", immobile: "Immobile", signal: "Perte de signal" };

const COLORS = ["#076633", "#ea580c", "#2563eb", "#9333ea", "#db2777", "#0891b2", "#ca8a04"];
const ago = (iso) => { if (!iso) return "jamais"; const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000); return m < 1 ? "à l'instant" : m < 60 ? `il y a ${m} min` : `il y a ${Math.floor(m / 60)} h ${m % 60} min`; };

function TourRow({ t, color }) {
  const stale = !t.last_position || Date.now() - new Date(t.last_position.at).getTime() > 10 * 60000;
  return (
    <div className={`rounded-xl border p-3 ${t.alerts?.length ? "bg-rose-50 border-rose-300" : "bg-white"}`} data-testid={`live-tour-${t.tricycle_code}`}>
      <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: color }} /><b className="font-mono">{t.tricycle_code}</b><span className="text-xs text-slate-500">{t.vendeur_name}</span></div>
      <p className="text-xs text-slate-500 mt-1">{t.circuit} · {t.visites} visite(s)</p>
      <p className="text-xs mt-1">CA <b className="font-mono">{money(t.ca)}</b> / {money(t.objectif_ca)}</p>
      <p className={`text-[11px] mt-1 ${stale ? "text-orange-600" : "text-brand-700"}`} data-testid={`live-last-${t.tricycle_code}`}>Position : {ago(t.last_position?.at)}</p>
      <Link to={`/vi/tournees/${t.id}`} className="text-[11px] text-brand-700 hover:underline">Ouvrir la tournée →</Link>
      {t.alerts?.length > 0 && <p className="text-[11px] font-semibold text-rose-600" data-testid={`live-alert-${t.tricycle_code}`}>⚠ {t.alerts.map((a) => ALERT_LABELS[a]).join(" · ")}</p>}
    </div>
  );
}

export default function ViLiveMap() {
  const [d, setD] = useState(null);
  const [placing, setPlacing] = useState("");
  const [draft, setDraft] = useState(null);
  const load = () => api.get("/itinerant/live").then((r) => setD(r.data)).catch(() => setD({ tours: [], warehouses: [] }));
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  if (!d) return <Loader />;
  const markers = [
    ...d.tours.filter((t) => t.last_position).map((t, i) => ({ id: t.id, pos: [t.last_position.lat, t.last_position.lng], label: t.tricycle_code.slice(-2), color: t.alerts?.length ? "#dc2626" : COLORS[i % COLORS.length], size: 34,
      popup: <span><b>{t.tricycle_code}</b> · {t.vendeur_name}<br />CA {money(t.ca)} · {ago(t.last_position.at)}{t.alerts?.length > 0 && <><br /><b className="text-rose-600">{t.alerts.map((a) => ALERT_LABELS[a]).join(", ")}</b></>}</span> })),
    ...d.warehouses.filter((w) => w.gps).map((w) => ({ id: `w-${w.id}`, pos: [w.gps.lat, w.gps.lng], label: "E", color: "#334155", popup: <b>Entrepôt {w.nom}</b> })),
  ];
  const lines = d.tours.map((t, i) => ({ id: t.id, points: t.track, color: COLORS[i % COLORS.length] }));
  const circles = [
    ...(d.zones || []).filter((z) => !draft || z.id !== draft.cid).map((z) => ({ id: z.id, center: [z.zone_geo.lat, z.zone_geo.lng], radiusKm: z.zone_geo.rayon_km, label: `Zone ${z.nom}` })),
    ...(draft?.lat != null ? [{ id: "draft", center: [draft.lat, draft.lng], radiusKm: Number(draft.rayon_km) || 0.1, color: "#ea580c", dashed: true, label: "Nouvelle zone" }] : []),
  ];
  const place = async ({ lat, lng }) => {
    if (draft) { setDraft({ ...draft, lat, lng }); return; }
    if (!placing) return;
    const r = await call(() => api.post(`/itinerant/warehouses/${placing}/position`, { lat, lng }), "Position de l'entrepôt enregistrée");
    if (r) { setPlacing(""); load(); }
  };
  return (
    <div data-testid="vi-live-map">
      <PageHeader title="Carte des tricycles" subtitle={`Positions en direct des tournées en cours · mise à jour toutes les 30 s · ${d.tours.length} tricycle(s) en tournée`}>
        <Link to="/vi/alertes-gps"><Button variant="outline" className={d.open_alerts ? "border-rose-400 text-rose-700" : ""} data-testid="live-alerts-link"><BellRing className="h-4 w-4 mr-1.5" />Alertes GPS{d.open_alerts ? ` (${d.open_alerts})` : ""}</Button></Link>
        <Button variant="outline" onClick={load} data-testid="live-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Actualiser</Button>
      </PageHeader>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="w-64"><Pick value={placing} onChange={setPlacing} placeholder="Placer un entrepôt sur la carte…" testid="live-wh-select" options={d.warehouses.map((w) => ({ value: w.id, label: `${w.nom}${w.gps ? " ✓" : ""}` }))} /></div>
        {placing && <p className="text-xs text-orange-700 pb-2" data-testid="live-wh-hint"><Warehouse className="inline h-4 w-4 mr-1" />Cliquez sur la carte à l'emplacement de l'entrepôt (point de départ des itinéraires).</p>}
        {!placing && <ZoneEditor circuits={d.circuits || []} zones={d.zones || []} draft={draft} onDraftChange={setDraft} onSaved={load} />}
      </div>
      <div className="grid lg:grid-cols-4 gap-3">
        <div className="lg:col-span-3"><ViMap markers={markers} lines={lines} circles={circles} onClick={place} height={540} /></div>
        <div className="space-y-2" data-testid="live-list">
          {d.tours.length === 0 && <p className="text-sm text-slate-400">Aucun tricycle en tournée.</p>}
          {d.tours.map((t, i) => <TourRow key={t.id} t={t} color={COLORS[i % COLORS.length]} />)}
        </div>
      </div>
    </div>
  );
}
